import { loader } from '@play-co/timestep-core';
import { Locale, localeRuleset } from 'src/replicant/ruleset/locale';
import { getUrlSearchParamValue } from '../utils';
import { track } from '../analytics/utils';

let currentLocale: Locale = localeRuleset.fallbackLocale;

// Map of loadable locales
export const loadableLocales: Record<Locale, boolean> = {
  en: true,
  ja: true,
};

// Map of cached locales
const cachedLocales: Partial<Record<Locale, any>> = {};

export function getBrowserLocale() {
  let locale =
    (process.env.IS_DEVELOPMENT && getUrlSearchParamValue('locale')) ||
    window.navigator.language ||
    'ja';

  locale = locale.replace('_', '-');
  if (locale.indexOf('-') !== -1) {
    locale = locale.split('-')[0];
  }

  locale = locale.toLowerCase();

  return locale as Locale;
}

export function getUsableLocale(choice: Locale): Locale {
  return loadableLocales[choice]
    ? (choice as Locale)
    : localeRuleset.fallbackLocale;
}

/** Path is determined in scripts/generate-font-data.ts */
export function getLocalePath(locale: Locale) {
  return `assets/i18n/${locale}.json`;
}

export async function loadLocale(locale: Locale) {
  if (!cachedLocales[locale]) {
    const localePath = getLocalePath(locale);

    try {
      cachedLocales[locale] = await loader.loadJSON(localePath); // eslint-disable-line require-atomic-updates
    } catch (e) {
      track('Debug_localeLoadFailed', {
        localePath,
        locale,
        error: e,
      });
    }
  }

  currentLocale = locale as Locale;
}

export function getStrings(): any {
  return cachedLocales[currentLocale];
}

export function getCurrentLocale(): Locale {
  return currentLocale;
}

export default function getString(
  key: string,
  substitutions?: { [key: string]: any },
  forceLocale?: Locale,
): string {
  const splitKeys = key.split('.');
  const lastKeyIndex = splitKeys.length - 1;

  let data = cachedLocales[forceLocale || currentLocale];

  // No cached locales, we can't do anything in this case, just fallback to key
  if (!data) {
    return key;
  }

  for (let i = 0; i < lastKeyIndex; i++) {
    if (data == null) break;
    data = data[splitKeys[i]];
  }

  let result: string | undefined = data && data[splitKeys[lastKeyIndex]];

  if (result === undefined) {
    track('Debug_TranslationKeyNotFound', {
      key,
      isLocaleLoaded: !!data,
      locale: currentLocale,
      forceLocale,
    });

    return key;
  }

  if (substitutions) {
    for (const token in substitutions) {
      result = result.replace('{' + token + '}', substitutions[token]);
    }
  }

  return result;
}
